import { useEffect, useRef, useState } from 'react';

// Reveals an element on first intersection — UB2 trigger for the
// "Selection of the Week" staggered fade-in. Disconnects the observer
// after the first hit, so revealed state is monotonic.
export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(
  threshold = 0.2,
): { ref: React.RefObject<T>; revealed: boolean } {
  const ref = useRef<T>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    if (typeof IntersectionObserver === 'undefined') {
      setRevealed(true);
      return;
    }
    // If the element is already within the viewport at mount, reveal
    // synchronously. Covers the case where a parent renders the target
    // tardively (e.g. after an async fetch resolves while the user is still
    // near the top of the page) — the IntersectionObserver attach callback
    // is async and can be missed by browsers if the layout has just settled.
    const rect = ref.current.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setRevealed(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, revealed };
}
